/**
 * Client-side image resize + recompression helper.
 *
 * Why this exists: a member raised that several uploaded images on the site
 * are huge originals (e.g. 1500×1000) displayed at thumbnail sizes (160×160).
 * Uploading the original means we ship ~3 MB to render ~25 KB of pixels.
 *
 * This helper takes a `File` (typically from an <input type="file"> or a
 * drag-and-drop event), draws it to a canvas at a constrained size, and
 * outputs a fresh `File` of type image/webp (with JPEG fallback for browsers
 * that don't encode WebP) suitable for re-uploading.
 *
 * Usage:
 *   const small = await optimizeImageFile(rawFile, { maxDim: 800, quality: 0.82 });
 *   await supabase.storage.from("team-assets").upload(path, small);
 *
 * Options:
 *   maxDim         — longest side in pixels (default 1200). Aspect preserved.
 *   quality        — 0–1 (default 0.82). WebP / JPEG quality factor.
 *   preferType     — "image/webp" (default) or "image/jpeg".
 *   skipIfSmaller  — if true and image is already <= maxDim, return original (default true).
 *
 * Returns: a `File` whose .name preserves the input name (extension swapped),
 * or the original `File` unchanged if it isn't an image or already small.
 */
export async function optimizeImageFile(file, options = {}) {
  const {
    maxDim = 1200,
    quality = 0.82,
    preferType = "image/webp",
    skipIfSmaller = true,
  } = options;

  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // GIFs would lose animation if recompressed. Leave them alone.
  if (file.type === "image/gif") return file;

  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);

  const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
  if (skipIfSmaller && longestSide <= maxDim) return file;

  const scale = Math.min(1, maxDim / longestSide);
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  // Try the preferred type first; if the browser refuses (very rare today), fall back to JPEG.
  const blob = await new Promise(resolve => canvas.toBlob(resolve, preferType, quality))
    || await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) return file;
  // If the optimized blob is actually bigger than the original (rare for huge
  // sources, possible for already-tiny PNGs), keep the original.
  if (blob.size >= file.size) return file;

  const newName = swapExtension(file.name, blob.type);
  return new File([blob], newName, { type: blob.type, lastModified: Date.now() });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("image load failed"));
    i.src = src;
  });
}

function swapExtension(name, mime) {
  const ext = mime === "image/webp" ? "webp" : mime === "image/jpeg" ? "jpg" : (name.split(".").pop() || "img");
  const base = name.replace(/\.[^./\\]+$/, "");
  return `${base}.${ext}`;
}
